import { Directive } from "@angular/core";
import { BaseComponent, CommunityService, UserService } from "@mypxplat/xplat/core";
import { of } from "rxjs";
import { map } from "rxjs/operators";

@Directive()
export abstract class CommunityBaseComponent extends BaseComponent {
  public skills: Array<any>;
  public betaCommunityTags: Array<any>;
  public mySkills: Array<any>;
  public showingMyChannels = false;
  public selectedChannel: any;
  public channels: Array<any>;
  public myChannels: Array<any>;
  public displayedChannels: Array<any>;

  public selectedTopic: any;
  public topics: Array<any>;

  constructor(
    userService: UserService,
    public communityService: CommunityService
  ) {
    super(userService);
    this.displayedChannels = [];
  }

  loadMySkills() {
    return this.communityService.getUserSkills().pipe(
      map((result) => {
        this.mySkills = result;
        return result;
      })
    );
  }

  initBetaCommunityTags(tags) {
    this.betaCommunityTags = tags;
    this.betaCommunityTags.sort((a, b) => (a.title > b.title ? 1 : -1));
    this.channels = [...tags];
    this.channels.sort((a, b) => (a.title > b.title ? 1 : -1));
    this.channels.unshift(...this.communityService.extraChannels.filter((item) => item.beta));
  }

  /**
   * @param result Skills/channels from paeapi (`GET /community/skills/` for FSP, or
   *               `GET /communities/:id/channels` for non-FSP). Merged with the four
   *               built-in sidebar filters (all_posts / my_posts / general / pinned_posts)
   *               defined in `CommunityService.extraChannels` — those show on every
   *               community regardless of its config.
   */
  initSkills(result) {
    this.skills = [...result];
    this.channels = result;
    this.myChannels = [];
    if (this.mySkills?.length && this.user?.active_subscription) {
      this.mySkills.forEach((skill) => {
        let channel = this.channels.filter((channel) => skill.skill_id == channel.id)[0];
        if (channel) {
          // Ensure channel has proper string_id for translation
          if (!channel.string_id && channel.id) {
            channel.string_id = channel.id.toString();
          }
          this.myChannels.push(channel);
        }
      });
    }
    // Titles are per-locale maps now (DynamoDB `community_channels`); pluck the English
    // string for sorting so channels stay in a deterministic order regardless of the
    // viewer's current language.
    const titleKey = (c: any) => (typeof c?.title === "object" ? c.title?.en || "" : c?.title || "");
    this.skills.sort((a, b) => titleKey(a).localeCompare(titleKey(b)));

    let activeExtraChannels = this.communityService.extraChannels.filter((item) => {
      if (item.active) {
        if (item.public) {
          return true;
        } else if (item.role && item.role.length) {
          if (!this.user) return false;
          let hasRole = false;
          item.role.forEach((role) => {
            if (this.user[role]) {
              hasRole = true;
            }
          });
          return hasRole;
        }
      }
      return false;
    });

    // Fallback if no active extras came back — unreachable in current code (the hardcoded
    // list is always non-empty), but kept as a defensive backstop so the sidebar always
    // has at least "All Posts".
    if (activeExtraChannels.length === 0) {
      activeExtraChannels = [
        {
          id: "all_posts",
          string_id: "all_posts",
          title: "All Posts",
          active: true,
          public: true,
        },
      ];
    }
    this.channels.unshift(...activeExtraChannels.filter((item) => item.active && item.alphabetical));
    this.myChannels.unshift(...activeExtraChannels.filter((item) => item.active && item.alphabetical));
    this.channels.sort((a, b) => titleKey(a).localeCompare(titleKey(b)));
    this.myChannels.sort((a, b) => titleKey(a).localeCompare(titleKey(b)));
    this.channels = [...activeExtraChannels.filter((item) => !item.alphabetical), ...this.channels];
    this.myChannels = [...activeExtraChannels.filter((item) => !item.alphabetical), ...this.myChannels];

    // Dedupe by `string_id` — the built-in extras use string ids ("all_posts", "my_posts",
    // "pinned_posts"); admin-authored channels come from `community_channels`. Built-in
    // extras are pushed first (unshift + prepend) so they win over any admin channel with
    // the same string_id, though a collision is unlikely since the built-ins are reserved.
    const dedupe = (arr: any[]) => {
      const seen = new Set<string>();
      const out: any[] = [];
      for (const item of arr) {
        const key = String(item?.string_id ?? item?.id ?? "");
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(item);
      }
      return out;
    };
    this.channels = dedupe(this.channels);
    this.myChannels = dedupe(this.myChannels);

    this.channels.forEach((channel) => {
      if (channel && !channel.string_id && channel.id) {
        channel.string_id = channel.id.toString();
      }
    });
    this.myChannels.forEach((channel) => {
      if (channel && !channel.string_id && channel.id) {
        channel.string_id = channel.id.toString();
      }
    });

    if (this.myChannels.length === 0) {
      this.myChannels = [...this.channels];
    }

    // Sidebar always shows every channel in this community. The prior "my channels" /
    // "all channels" toggle (scoped to skills the user had added to their profile) was
    // removed in DENG-728 — community channels are curated by admins now, not filtered
    // by user-profile skills. `myChannels` / `showingMyChannels` remain on the base for
    // beta compatibility but the public community always renders `this.channels`.
    this.displayedChannels = this.channels;
    this.showingMyChannels = false;
  }
}
